// The five resource scan types Amazon Inspector accepts, transcribed from
// inspectorResourceScanTypes in backend/services/inspector_service.go. That
// allowlist is closed — an unrecognised string fails the whole request with a 400
// at the edge — so this list is a wire contract, not a menu. The setup wizard and
// the settings page both render it, and they used to keep private copies: any
// drift between them surfaced as a save that is accepted on one screen and
// rejected on the other, which is a miserable bug to diagnose from a support
// ticket.
//
// DELIBERATELY NOT derived from the Inspector engine descriptor's
// `capability.items` in engines/inspector.js, which legitimately carries only
// FOUR entries. That list is reader-facing marketing copy and folds LAMBDA and
// LAMBDA_CODE into a single "Lambda functions" bullet; driving checkboxes off it
// would make one accepted scan type permanently unselectable, with nothing in the
// UI to hint at it. The two lists differ in length on purpose — neither is a bug
// to be "fixed" against the other.
//
// `value` is the string sent on the wire. The key name matches the { value, label }
// shape MultiSelectField and AWS_REGIONS already use elsewhere in this folder.
//
// The LAMBDA / LAMBDA_CODE wording is load-bearing rather than stylistic: they are
// separately billed scans of different things (the deployed package versus the
// customer's own source), and a reader who cannot tell them apart pays for the
// wrong one. Keep the contrast explicit in both the label and the description.
//
// Inspector bills PER RESOURCE SCANNED rather than per account, so `price` is a
// decision input on both screens, not decoration. Anything rendering it must also
// render SCAN_TYPE_PRICE_DISCLAIMER — these are indicative us-east-1 list prices,
// and an undisclaimed figure reads as a quote.
export const INSPECTOR_SCAN_TYPES = [
  {
    value: 'EC2',
    label: 'EC2 instances',
    description: 'Operating-system and application package vulnerabilities on running instances, plus unintended network exposure.',
    price: '≈ $1.26 per instance / month',
  },
  {
    value: 'ECR',
    label: 'ECR container images',
    description: 'Package vulnerabilities in images pushed to Amazon ECR, re-scanned as new CVEs are published.',
    price: '≈ $0.09 per initial image scan',
  },
  {
    value: 'LAMBDA',
    label: 'Lambda functions — dependencies',
    description: 'The deployed package and its layers, scanned for vulnerable third-party dependencies. Your own source is not read.',
    price: '≈ $0.30 per function / month',
  },
  {
    value: 'LAMBDA_CODE',
    label: 'Lambda functions — your code',
    description: 'The function’s own source, scanned for insecure code — injection, hard-coded secrets, weak crypto. Adds to the dependency scan rather than replacing it, and is billed on top of it.',
    price: '≈ $0.90 per function / month',
  },
  {
    value: 'CODE_REPOSITORY',
    label: 'Code repositories',
    description: 'Connected source repositories, scanned for insecure code, exposed secrets, and vulnerable dependencies declared in their manifests.',
    price: 'see AWS Inspector pricing',
  },
];

// labelForScanType renders a stored wire value for display. A stored config can
// legitimately carry a value this list does not know — an older tenant enabled
// before a type was added, or a type AWS has since renamed — so an unknown value
// falls back to itself rather than to an empty cell that reads as "none".
export const labelForScanType = (value) =>
  INSPECTOR_SCAN_TYPES.find((t) => t.value === value)?.label || value;

export const SCAN_TYPE_PRICE_DISCLAIMER ='Prices are indicative us-east-1 list prices for orientation only — actual charges vary by region, by scan volume and by AWS pricing changes. Check the AWS Inspector pricing page before enabling a surface at scale.';
